import React, { useState, useEffect, useMemo } from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';

import Base from '@/templates/Base/Base';

import Hero from '@Theme/natucare/modules/V4/Hero/Hero';
import MediaBar from '@Theme/natucare/modules/V4/MediaBar/MediaBar';
import Categories from '@Theme/natucare/modules/V4/Categories/Categories';
import Products from '@Theme/natucare/modules/V4/Products/Products';
import Standards from '@Theme/natucare/modules/V4/Standards/Standards';
import Features from '@Theme/natucare/modules/V4/Features/Features';
import Research from '@Theme/natucare/modules/V4/Research/Research';
import Reviews from '@Theme/natucare/modules/V4/Reviews/Reviews';
import Subscription from '@Theme/natucare/modules/V4/Subscription/Subscription';
import TrustmateWidget from '@Theme/natucare/modules/V4/TrustmateWidget/TrustmateWidget';
import Blog from '@Theme/natucare/modules/V4/Blog/Blog';

import Seo from '@/components/Seo/Seo';

import { parsePosts } from '@/templates/Blog/utils/parsePosts';

import DATA from '@/data/pl/index.data';

const HOMEPAGE_COLLECTIONS = [
  { handle: 'bestsellery', fallbackLabel: 'BESTSELLERY' },
  { handle: 'nowosci', fallbackLabel: 'NOWOŚCI' }
];

const BLOG_TABS_CONFIGURATION = [
  {
    label: 'Wyróżnione',
    order: [1458, 1207, 1370, 4891],
    sources: ['featuredPosts']
  },
  {
    label: 'Kolagen',
    order: [888, 1196, 982, 1482],
    sources: ['kolagenPosts']
  },
  {
    label: 'Omega-3',
    order: [1672, 15, 996, 998],
    sources: ['omegaPosts', 'omegaLists']
  },
  {
    label: 'Odchudzanie',
    order: [1317, 4891, 1098, 25],
    sources: ['odchudzaniePosts', 'odchudzanieLists']
  },
  {
    label: 'Jelita i metabolizm',
    order: [1456, 1207, 928, 928],
    sources: ['jelitaPosts']
  },
  {
    label: 'Witaminy i minerały',
    order: [1150, 1164, 932, 27],
    sources: ['witaminyPosts', 'witaminyLists']
  }
];

const priceFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN'
});

const formatPrice = value => {
  const numericValue = typeof value === 'string'
    ? Number.parseFloat(value)
    : typeof value === 'number'
      ? value
      : Number.NaN;

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return priceFormatter.format(numericValue);
};

const parseRatingValue = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');

    if (!normalized) {
      return null;
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const mapProductEdgeToModuleItem = edge => {
  const node = edge?.node;

  if (!node) {
    return null;
  }

  const variantNode = node.variants?.edges?.[0]?.node;
  const price = formatPrice(variantNode?.price);
  const customFeaturedImageReference = node.customFeaturedImage?.reference;
  const customFeaturedImage = customFeaturedImageReference?.image;
  const customCollectionName = node.customCollectionName?.value;
  const customPreviewImage = customFeaturedImageReference?.previewImage;
  const imageUrl = customFeaturedImage?.url || customPreviewImage?.url || node.featuredImage?.url;
  const imageAlt = customFeaturedImage?.altText || node.featuredImage?.altText || node.title;

  return {
    id: node.id || node.handle || node.title,
    title: node.customTitle?.value || node.title,
    category: customCollectionName,
    price: price || '',
    rating: parseRatingValue(node.customRating?.value),
    link: node.onlineStoreUrl,
    image: imageUrl
      ? {
        desktop: { png: imageUrl },
        mobile: { png: imageUrl },
        alt: imageAlt
      }
      : null
  };
};

const sortEdgesByPreferredOrder = (edges, preferredOrder = []) => {
  if (!Array.isArray(edges) || edges.length === 0) {
    return [];
  }

  const orderMap = new Map(preferredOrder.map((id, index) => [Number(id), index]));
  const fallbackStart = preferredOrder.length;

  return edges
    .filter(edge => edge?.node)
    .map((edge, index) => {
      const nodeId = Number(edge.node?.strapi_id);
      const preferredIndex = orderMap.has(nodeId) ? orderMap.get(nodeId) : fallbackStart + index;

      return {
        edge,
        index: preferredIndex
      };
    })
    .sort((a, b) => a.index - b.index)
    .map(item => item.edge);
};

const mapParsedPostToArticle = item => {
  if (!item) {
    return null;
  }

  const imageSrc = item.picture?.src;
  const heading = item.heading;

  return {
    heading,
    description: item.description,
    link: item.href,
    category: typeof item.category === 'string' ? item.category : item.category?.label,
    image: imageSrc
      ? {
        mobile: { src: imageSrc },
        desktop: { src: imageSrc },
        alt: item.picture?.alt || heading
      }
      : null
  };
};

const HomePage = props => {
  const postsModel = get(props, 'data');
  const { headerData, footerData } = props.pageContext;

  const blogTabs = useMemo(() => {
    if (!postsModel) {
      return [];
    }

    return BLOG_TABS_CONFIGURATION
      .map(({ label, sources, order }) => {
        const combinedEdges = sources.flatMap(sourceKey => get(postsModel, `${sourceKey}.edges`, []));
        const sortedEdges = sortEdgesByPreferredOrder(combinedEdges, order);
        const parsedItems = parsePosts({ postsModel: sortedEdges }) || [];
        const articles = parsedItems
          .map(mapParsedPostToArticle)
          .filter(Boolean);

        if (!articles.length) {
          return null;
        }

        return {
          label,
          articles
        };
      })
      .filter(Boolean);
  }, [postsModel]);

  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isSending: false,
    isSubscribed: false,
    isEmailTaken: false
  });

  const handleSubscribeNewsletter = async (email, tags = 'newsletter') => {
    setSubscriptionStatus({
      isSending: true,
      isSubscribed: false,
      isEmailTaken: false
    });

    try {
      const response = await fetch('/api/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          tags
        })
      });

      const data = await response.json();


      setSubscriptionStatus({
        isSending: false,
        isSubscribed: !data.errorCode,
        isEmailTaken: data.errorCode === 'EMAIL_ALREADY_TAKEN'
      });

      if (typeof data.errorCode === 'undefined' && data?.errorCode !== 'EMAIL_ALREADY_TAKEN') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ 'event': 'subscribe_nwl' });
      }

    } catch (error) {
      setSubscriptionStatus({
        isSending: false,
        isSubscribed: false,
        isEmailTaken: false
      });
    }
  };

  const [productTabs, setProductTabs] = useState([]);

  useEffect(() => {
    const fetchProductsForHandle = async ({ handle, fallbackLabel }) => {
      try {
        const response = await fetch(`/api/get-products?handle=${encodeURIComponent(handle)}`);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const items = Array.isArray(payload?.products)
          ? payload.products
            .map(mapProductEdgeToModuleItem)
            .filter(Boolean)
          : [];

        if (!items.length) {
          return null;
        }

        const label = fallbackLabel;

        return {
          label,
          items
        };
      } catch (error) {
        console.error(`Shopify fetch error for handle ${handle}:`, error);
        return null;
      }
    };

    const fetchProducts = async () => {
      const tabs = await Promise.all(HOMEPAGE_COLLECTIONS.map(fetchProductsForHandle));
      setProductTabs(tabs.filter(Boolean));
    };

    fetchProducts();
  }, []);

  const shouldRenderProducts = productTabs.length > 0;

  DATA.blog = {
    heading: 'Wiedza i nauka',
    tabs: blogTabs
  };

  return <Base headerData={headerData} footerData={footerData} tidio isKlaviyoPl={true} locale="pl">
    <Seo
      lang='pl'
      title='Natu.Care - Suplementy Premium'
      description='Natu.Care powstało po to, aby zapewnić kompleksowe wsparcie dla Twojego organizmu. Nasze produkty zawierają wyłącznie niezbędne do prawidłowego funkcjonowania składniki.'
      structuredData={DATA.schema}
      hrefLang={DATA.hrefLang}
    />
    <Hero {...DATA.hero} />
    <MediaBar {...DATA.mediaBar} />
    <Categories {...DATA.categories} />
    {shouldRenderProducts && (
      <Products {...DATA.products} tabs={productTabs} />
    )}
    <Standards {...DATA.standards} />
    <Features {...DATA.features} />
    <Research {...DATA.research} />
    <Reviews {...DATA.reviews} />
    <Subscription {...DATA.subscription} />
    <Blog {...DATA.blog} />
    <TrustmateWidget {...DATA.trustmatewidget} />

  </Base>;
};

export default HomePage;

export const pageQuery = graphql`
  query GetPostsForHomepage($locale: String! = "pl") {
    featuredPosts: allStrapiBlogPost(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        strapi_id: { in: [1458, 1207, 1370, 4891] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          category {
            slug
            heading
            shortHeading
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    kolagenPosts: allStrapiBlogPost(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [888, 1196, 982, 1482] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          category {
            slug
            heading
            shortHeading
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    omegaPosts: allStrapiBlogPost(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [1672, 996, 998] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          category {
            slug
            heading
            shortHeading
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    omegaLists: allStrapiBlogList(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [15] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    odchudzaniePosts: allStrapiBlogPost(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [1317, 4891, 1098] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          category {
            slug
            heading
            shortHeading
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    odchudzanieLists: allStrapiBlogList(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [25] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    jelitaPosts: allStrapiBlogPost(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [1456, 1207, 928] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          category {
            slug
            heading
            shortHeading
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    witaminyPosts: allStrapiBlogPost(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [1150, 1164, 932] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          category {
            slug
            heading
            shortHeading
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }

    witaminyLists: allStrapiBlogList(
      filter: {
        locale: { eq: "pl" }
        slug: { ne: null }
        options: { strapi_json_value: { nin: "hideInListing" } }
        strapi_id: { in: [27] }
      }
    ) {
      edges {
        node {
          strapi_id
          locale
          slug
          shortHeading
          heading
          shortContent
          seo {
            title
          }
          thumbnailImage {
            url
          }
          createdAtFullDate: createdAtCustom(locale: $locale, formatString: "DD MMMM")
          createdAtShortDate: createdAtCustom(locale: $locale, formatString: "DD MMM")
          dateFormat: createdAtCustom(locale: $locale, formatString: "YYYY-MM-DD")
        }
      }
    }
  }
`;
